// Thin fetch wrapper for the Pages Functions API. All requests are same-origin
// and rely on the HttpOnly session cookie for auth.

import type { Collection, Keyword, Photo } from './types';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export const api = {
  // --- auth ---
  async session(): Promise<boolean> {
    const { authenticated } = await req<{ authenticated: boolean }>(
      '/api/session',
    );
    return authenticated;
  },
  async login(password: string): Promise<void> {
    await req('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
  },
  async logout(): Promise<void> {
    await req('/api/logout', { method: 'POST' });
  },

  // --- library ---
  async listPhotos(params: Record<string, string>): Promise<Photo[]> {
    const qs = new URLSearchParams(params).toString();
    const { photos } = await req<{ photos: Photo[] }>(
      `/api/photos${qs ? `?${qs}` : ''}`,
    );
    return photos;
  },
  async uploadPhoto(form: FormData): Promise<{ id: string }> {
    return req<{ id: string }>('/api/photos/upload', {
      method: 'POST',
      body: form,
    });
  },
  async updatePhoto(
    id: string,
    patch: Record<string, unknown>,
  ): Promise<void> {
    await req(`/api/photos/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
  },
  async deletePhoto(id: string): Promise<void> {
    await req(`/api/photos/${id}`, { method: 'DELETE' });
  },
  async setKeywords(id: string, keywords: string[]): Promise<void> {
    await req(`/api/photos/${id}/keywords`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keywords }),
    });
  },

  // --- collections ---
  async listCollections(): Promise<Collection[]> {
    const { collections } = await req<{ collections: Collection[] }>(
      '/api/collections',
    );
    return collections;
  },
  async createCollection(name: string): Promise<Collection> {
    return req<Collection>('/api/collections', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
  },
  async addToCollection(collectionId: string, photoId: string): Promise<void> {
    await req(`/api/collections/${collectionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photoId }),
    });
  },
  async removeFromCollection(
    collectionId: string,
    photoId: string,
  ): Promise<void> {
    await req(`/api/collections/${collectionId}?photoId=${photoId}`, {
      method: 'DELETE',
    });
  },
  async deleteCollection(collectionId: string): Promise<void> {
    await req(`/api/collections/${collectionId}`, { method: 'DELETE' });
  },

  // --- keywords ---
  async listKeywords(): Promise<Keyword[]> {
    const { keywords } = await req<{ keywords: Keyword[] }>('/api/keywords');
    return keywords;
  },

  // --- edits ---
  async getEdits<T>(id: string): Promise<T> {
    const { settings } = await req<{ settings: T }>(`/api/edits/${id}`);
    return settings;
  },
  async saveEdits<T>(id: string, settings: T): Promise<void> {
    await req(`/api/edits/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings }),
    });
  },
};
